// src/portal-app/operations/kit.js
//
// W3 Task 1 — Operations presentation kit. Shared across the four W3 surfaces:
// Transactions, EOD, Expenses, Reservations. Presentation-only — no data
// fetching, no direct service calls.
//
// Exports:
//   ConfirmSheet    — re-exported from shell/kit (Plan 4 Task 1 move)
//   SegmentedControl— re-exported from shell/kit (Plan 4 Task 1 move)
//   StatusBadge     — re-exported from shell/kit (Plan 4 Task 1 move)
//   SearchInput     — re-exported from shell/kit (Plan 4 Task 1 move)
//   FilterRow       — horizontally scrollable filter chip strip
//   OpSummaryBar    — summary totals bar (revenue · cash · card · …)
//
// ConfirmSheet, SegmentedControl, StatusBadge and SearchInput are
// cross-cutting (spec §6) — they moved to shell/kit so Team/More screens
// stop reaching into an Operations module for them. They're re-exported here
// so the 19 existing importers of this file keep working untouched. Do NOT
// import from '../shell/kit' back into this file for anything other than
// re-exporting these four — the dependency direction is operations -> shell,
// never the reverse.
//
// Design rules:
//   • Tokens only — every colour is var(--c-*) from theme.css; translucency
//     via color-mix(in srgb, var(--c-*) N%, transparent).
//   • font-variant-numeric: tabular-nums (.opk-num) on every money/number.
//   • A11y: primary text ≥4.5:1 on glass; touch targets ≥44pt;
//     --safe-top/--safe-bottom respected; prefers-reduced-motion honoured;
//     inline SVG only — no emoji.
//   • i18n: every user-visible string via t('key', {}, 'English fallback').
//   • RTL-safe: logical properties (margin-inline-*, padding-inline-*) used
//     throughout; flexbox for inline alignment.
//   • POS byte-unchanged: nothing touches src/pos-app/, src/pages/portal/,
//     src/services/, or src/context/.
import React from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { useLanguage } from '../../context/LanguageContext';

export { ConfirmSheet, SegmentedControl, StatusBadge, SearchInput } from '../shell/kit';

/* ------------------------------------------------------------------ */
/* Shared stylesheet — injected once (LiquidGlass ensureCss pattern). */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- tabular numerals ---- */
/* Also defined in shell/kit/kitCss.js — ConfirmSheet/SegmentedControl/
   StatusBadge/SearchInput moved there (Plan 4 Task 1) but OpSummaryBar
   stayed here and still needs it. Duplicated deliberately: an identical
   rule in both sheets is harmless, a missing one silently drops tabular
   numerals. */
.opk-num{font-variant-numeric:tabular-nums;}

/* ---- FilterRow ---- */
.opk-filters{display:flex;gap:8px;overflow-x:auto;padding:2px;margin:-2px;
  -webkit-overflow-scrolling:touch;scrollbar-width:none;}
.opk-filters::-webkit-scrollbar{display:none;}
.opk-chip{flex:none;display:inline-flex;align-items:center;
  min-height:44px;padding:0 16px;border-radius:999px;cursor:pointer;
  font-family:inherit;font-size:13px;font-weight:600;white-space:nowrap;
  color:var(--c-textLight);
  background:color-mix(in srgb,var(--c-surface) 38%,transparent);
  border:1px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
  transition:transform .12s ease,background .15s ease;}
.opk-chip:active{transform:scale(.96);}
.opk-chip-active{background:var(--c-green);border-color:var(--c-green);
  color:var(--c-white);font-weight:700;}

/* ---- OpSummaryBar ---- */
.opk-summary{display:flex;flex-wrap:wrap;align-items:center;gap:4px 16px;
  padding:12px 16px;}
.opk-summary-item{display:flex;align-items:center;gap:6px;}
.opk-summary-label{font-size:11px;font-weight:600;letter-spacing:.4px;
  text-transform:uppercase;color:var(--c-textLight);}
.opk-summary-value{font-size:13px;font-weight:700;color:var(--c-ink);}
.opk-summary-sep{color:var(--c-mutedLight);font-size:13px;user-select:none;}

@media (prefers-reduced-motion: reduce){
  .opk-chip,.opk-chip:active{transition:none;transform:none;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'opk-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* FilterRow — horizontal scrollable filter chip strip.               */
/* "All" chip (value=null) prepended. Active chip highlighted.        */
/* Horizontally scrollable — the ONE approved horizontal-scroll       */
/* exception per the brief (like RangeBar in the insights kit).       */
/* props:                                                              */
/*   filters   [{id, label}]                                           */
/*   value     string|null  active filter id (null = All)             */
/*   onChange  (id|null) => void                                       */
/*   allLabel  string  label for "All" chip                           */
/* ------------------------------------------------------------------ */
export function FilterRow({ filters = [], value, onChange, allLabel }) {
    ensureCss();
    const { t } = useLanguage();
    const all = allLabel || t('all', {}, 'All');

    return (
        <div className="opk-filters" role="group">
            {/* All chip */}
            <button
                type="button"
                className={`opk-chip${value === null ? ' opk-chip-active' : ''}`}
                data-testid="filter-chip-all"
                onClick={() => onChange(null)}
            >
                {all}
            </button>
            {filters.map((f) => {
                const key = f.value ?? f.id;
                return (
                    <button
                        key={key}
                        type="button"
                        className={`opk-chip${value === key ? ' opk-chip-active' : ''}`}
                        data-testid={`filter-chip-${key}`}
                        onClick={() => onChange(key)}
                    >
                        {f.label}
                    </button>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* OpSummaryBar — compact horizontal bar of label-value pairs.        */
/* Glass-backed, small text, tabular numerals.                        */
/* Renders nothing when items is empty.                               */
/* props:                                                              */
/*   items  [{label, value}]  e.g. Revenue + £1,234                  */
/* ------------------------------------------------------------------ */
export function OpSummaryBar({ items = [] }) {
    ensureCss();
    if (!items.length) return null;
    return (
        <GlassCard veil className="opk-summary" data-testid="op-summary-bar">
            {items.map((item, i) => (
                <React.Fragment key={`${item.label || ''}#${i}`}>
                    {i > 0 && <span className="opk-summary-sep" aria-hidden="true">·</span>}
                    <span className="opk-summary-item">
                        <span className="opk-summary-label">{item.label}</span>
                        <span
                            className="opk-summary-value opk-num"
                            data-testid="summary-value"
                        >
                            {item.value}
                        </span>
                    </span>
                </React.Fragment>
            ))}
        </GlassCard>
    );
}
